/**
 * pipelinePlace — the CRM pipeline board's PLACE, and the per-user memory of
 * which pipeline a person works in (Card PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026).
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * MEASURED on `main` @ d0cc155 (tests/react/pipelinePlaceRoundTrip.test.js
 * drives it): CRMPage renders the pipeline as
 *
 *     {!selectedContactId && tab === 'pipeline' && <PipelineTab …/>}
 *
 * so opening a contact UNMOUNTS the whole tab. The Contacts tab next to it is
 * deliberately kept mounted-but-hidden for exactly this reason; the pipeline
 * never was. Everything the component held went with it: which pipeline was
 * open, which board view, which stage column you were reading, how far down it
 * you had scrolled, and every extra page you had pressed "Load more" for.
 *
 * ── ⭐ ONE MECHANISM, NOT TWO ───────────────────────────────────────────────
 *
 * The obvious alternative was to copy the Contacts tab and keep the pipeline
 * mounted-but-hidden. It was rejected on purpose:
 *
 *   1. `display:none` DESTROYS SCROLL ANYWAY. A hidden element has no layout
 *      box, so the browser resets scrollTop and does not restore it on unhide.
 *      The Contacts tab already has to capture and restore its scroll by hand
 *      across that transition (CRMPage `listScrollTopRef`) — hiding buys the
 *      data but not the place, so a place record is needed either way.
 *   2. A RESTORE PATH THAT ONLY RUNS ON RELOAD WOULD ROT. If Back were served
 *      by staying mounted, the serialise/restore code below would execute only
 *      on an F5 — rarely, and never in the demo. The same code now runs on
 *      EVERY return from a contact, so a regression in it is immediately
 *      visible rather than discovered during go-live.
 *
 * ── WHAT IS STORED, WHERE, AND WHY THOSE TWO MEDIA ──────────────────────────
 *
 *   CHOICE  — which pipeline this person works in. localStorage, per user.
 *             It is a preference that should outlive the browser tab ("the
 *             pipeline I always open"), which is what the card asks for.
 *   PLACE   — stage, scroll offsets, loaded page counts, board view.
 *             sessionStorage, per user AND per pipeline. Tab-scoped and gone
 *             when the tab closes, matching CrmFilterBar's reasoning for the
 *             CRM search box: a place is a property of THIS sitting at THIS
 *             desk, not a setting to carry to the next one.
 *
 * ── ⛔⛔ NOTHING HERE CAN CARRY A NAME ──────────────────────────────────────
 *
 * Same posture as src/lib/urlState.js, and for the same reason: browser storage
 * on a shared workstation is plaintext. Every field in both records is an
 * INTEGER — a pipeline id, a stage id, a pixel offset, a page count — and the
 * sanitiser below is applied on WRITE as well as on READ, so a future caller
 * cannot put a contact name, a search term or a note into these keys by passing
 * one: there is no field that would accept it. The restriction is structural,
 * not a convention someone has to remember.
 *
 * ⚠ A stage id and a pipeline id name a CONFIGURATION ROW, not a person. They
 * are strictly less disclosive than the `?contact=` id that Card
 * PARTNER-LIST-REFRESH already accepted into the address bar.
 *
 * ── EVERY READ IS TOTAL ─────────────────────────────────────────────────────
 *
 * localStorage throws outright in a Safari private window and when a browser is
 * configured to block site data; it also returns whatever a previous build (or
 * a person with devtools open) left behind. So every reader here is wrapped and
 * every reader validates the shape it got back. An unreadable, absent, or
 * malformed record is not an error state — it degrades to "no opinion", the
 * caller falls through to its own default, and the board renders.
 */
"use strict";

const CHOICE_PREFIX = "zg.crm.pipeline.v1.";       // localStorage  — per user
const PLACE_PREFIX = "zg.crm.pipelineplace.v1.";   // sessionStorage — per user+pipeline

/**
 * The id shape, matching src/lib/urlState.js exactly. Pipeline and stage ids
 * are BIGSERIAL and arrive from pg as strings, so this is a STRING comparison
 * and never Number() — the same deliberate choice urlState records.
 */
const ID_RE = /^[1-9][0-9]{0,18}$/;

/** @returns {string} the id as a string when it is a legal id, else ''. */
export function normalizeId(value) {
  if (value == null) return "";
  const s = String(value);
  return ID_RE.test(s) ? s : "";
}

/**
 * A scroll offset or a page count: a non-negative integer, capped.
 *
 * ⚠ THE CAP IS NOT COSMETIC. A restored scrollTop is written straight onto a
 * DOM node; a stored Infinity, NaN or 1e30 (hand-edited, or left by a bug)
 * would be either ignored or clamped by the browser with no signal, which is
 * the silent no-op failure NAV-1 already paid for once. Anything outside the
 * range is dropped to 0 — "top of the column", a place that always exists.
 */
const MAX_OFFSET = 10_000_000;
function normalizeOffset(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > MAX_OFFSET) return 0;
  return Math.floor(n);
}

/** The board's two view modes. Anything else is not a view and becomes 'board'. */
const VIEWS = Object.freeze(["board", "legacy"]);
function normalizeView(value) {
  return VIEWS.includes(value) ? value : "board";
}

/** The shape a caller gets when there is nothing to restore. Frozen: callers must not mutate the default. */
export const EMPTY_PLACE = Object.freeze({
  stageId: "",
  boardX: 0,
  columnY: Object.freeze({}),
  loadedExtra: Object.freeze({}),
  view: "board",
});

function storageOr(explicit, which) {
  if (explicit) return explicit;
  try {
    if (typeof window === "undefined") return null;
    return which === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    // Accessing the property ITSELF throws in some privacy configurations —
    // not merely getItem. This is why the guard is around the access and not
    // only around the call below.
    return null;
  }
}

const choiceKey = (userId) => CHOICE_PREFIX + String(userId == null ? "anon" : userId);
const placeKey = (userId, pipelineId) =>
  `${PLACE_PREFIX}${String(userId == null ? "anon" : userId)}.${normalizeId(pipelineId)}`;

// ── CHOICE: which pipeline this person works in ────────────────────────────

/**
 * @returns {string} the remembered pipeline id, or '' when there is none,
 *   storage is unavailable, or the stored value is not an id.
 */
export function loadPipelineChoice(userId, storage) {
  const target = storageOr(storage, "local");
  if (!target) return "";
  try {
    return normalizeId(target.getItem(choiceKey(userId)));
  } catch {
    return "";
  }
}

/**
 * Remember the pipeline. A value that is not an id CLEARS the record rather
 * than writing junk a later read would have to reject — one validator, applied
 * at both ends.
 */
export function savePipelineChoice(userId, pipelineId, storage) {
  const target = storageOr(storage, "local");
  if (!target) return;
  try {
    const id = normalizeId(pipelineId);
    if (id) target.setItem(choiceKey(userId), id);
    else target.removeItem(choiceKey(userId));
  } catch {
    /* quota, private mode, or site data blocked — the choice just won't persist */
  }
}

// ── PLACE: where in the board this person was ──────────────────────────────

/**
 * Coerce anything at all into a legal place record.
 *
 * Exported because it is the single definition of the shape, and the WRITER
 * runs it too: a caller cannot store a field this would reject.
 */
export function sanitizePlace(raw) {
  if (!raw || typeof raw !== "object") return EMPTY_PLACE;
  const columnY = {};
  const loadedExtra = {};
  const src = raw.columnY && typeof raw.columnY === "object" ? raw.columnY : {};
  for (const k of Object.keys(src)) {
    const id = normalizeId(k);
    if (!id) continue;                 // a non-id key is dropped, never carried
    const y = normalizeOffset(src[k]);
    if (y > 0) columnY[id] = y;        // 0 is the default; storing it is noise
  }
  const lsrc = raw.loadedExtra && typeof raw.loadedExtra === "object" ? raw.loadedExtra : {};
  for (const k of Object.keys(lsrc)) {
    const id = normalizeId(k);
    if (!id) continue;
    const n = normalizeOffset(lsrc[k]);
    if (n > 0) loadedExtra[id] = n;
  }
  return {
    stageId: normalizeId(raw.stageId),
    boardX: normalizeOffset(raw.boardX),
    columnY,
    loadedExtra,
    view: normalizeView(raw.view),
  };
}

/** @returns {object} a sanitised place record — EMPTY_PLACE when there is nothing usable. */
export function loadBoardPlace(userId, pipelineId, storage) {
  if (!normalizeId(pipelineId)) return EMPTY_PLACE;
  const target = storageOr(storage, "session");
  if (!target) return EMPTY_PLACE;
  try {
    const raw = target.getItem(placeKey(userId, pipelineId));
    if (!raw) return EMPTY_PLACE;
    return sanitizePlace(JSON.parse(raw));
  } catch {
    // Unreadable storage OR unparseable JSON left by anything at all. Both mean
    // the same thing to the caller: no opinion about where they were.
    return EMPTY_PLACE;
  }
}

/** Store the place for one pipeline. Sanitised first — see sanitizePlace. */
export function saveBoardPlace(userId, pipelineId, place, storage) {
  if (!normalizeId(pipelineId)) return;
  const target = storageOr(storage, "session");
  if (!target) return;
  try {
    target.setItem(placeKey(userId, pipelineId), JSON.stringify(sanitizePlace(place)));
  } catch {
    /* quota or unavailable — the place just won't survive */
  }
}

/**
 * Resolve which pipeline to show, in the order Card PARTNER-LIST-REFRESH
 * established for the CRM section tab and `tests/react/CrmUrlPlaceWiring.test.js`
 * pins:
 *
 *     URL  →  per-user memory  →  the default pipeline  →  the first one
 *
 * ⚠ THE ORDER IS THE POINT, not merely a preference. `?pipeline=` is present
 * only when this browser was already here (a reload) or when a colleague pasted
 * a link; localStorage is "what I usually open". When they disagree the address
 * bar is the more recent intent, and it is the only one of the two that can
 * carry a deep link. Memory ahead of the URL would silently open the wrong
 * board for a pasted link — the same defect NAV-4 fixed one level up.
 *
 * ⚠ EVERY SOURCE IS VALIDATED AGAINST THE PIPELINES ACTUALLY ON OFFER. A
 * remembered id that was archived, deleted, or that this person may not see
 * falls through to the next source instead of rendering an empty board. This
 * is also why remembering a choice CANNOT widen access: the id is only ever
 * used to pick from the list the server already returned.
 *
 * @param {Array}  pipelines  as returned by crm:pipelines:list
 * @param {string} fromUrl    `?pipeline=` (already validated by urlState)
 * @param {string} remembered loadPipelineChoice()
 * @returns {string} the id to show, or '' when there are no pipelines at all.
 */
export function resolvePipelineId(pipelines, fromUrl, remembered) {
  const list = Array.isArray(pipelines) ? pipelines : [];
  if (list.length === 0) return "";
  const has = (id) => (id ? list.some((p) => p && String(p.id) === String(id)) : false);
  const url = normalizeId(fromUrl);
  if (has(url)) return url;
  const mem = normalizeId(remembered);
  if (has(mem)) return mem;
  const def = list.find((p) => p && p.is_default);
  if (def) return String(def.id);
  return String(list[0].id);
}
